import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { markReleaseNoteSeen } from '@/client/sdk.gen'
import type { ReleaseNotePublicResponse } from '@/client/types.gen'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { WhatsNewMarkdown } from '@/components/whats-new/WhatsNewMarkdown'
import { useAuth } from '@/context/AuthContext'
import { useTranslation } from '@/hooks/useTranslation'
import { useWhatsNew } from '@/hooks/useWhatsNew'
import { submitWhatsNewFeatureRequestFeedback } from '@/services/featureRequestFeedback'
import { isSentryUserFeedbackAvailable } from '@/services/sentry'
import { captureUnexpectedError } from '@/utils/error'

type WhatsNewDialogProps = {
	open: boolean
	note: ReleaseNotePublicResponse | null
	onDismiss: () => void
}

export function WhatsNewDialog({ open, note, onDismiss }: WhatsNewDialogProps) {
	const { t } = useTranslation()
	const { user, login } = useAuth()
	const [dismissing, setDismissing] = useState(false)
	const [showFeatureRequest, setShowFeatureRequest] = useState(false)
	const [description, setDescription] = useState('')
	const [descriptionError, setDescriptionError] = useState(false)
	const [submitting, setSubmitting] = useState(false)

	const resetFeatureRequestForm = useCallback(() => {
		setShowFeatureRequest(false)
		setDescription('')
		setDescriptionError(false)
		setSubmitting(false)
	}, [])

	useEffect(() => {
		if (!open) {
			resetFeatureRequestForm()
		}
	}, [open, resetFeatureRequestForm])

	const handleDismiss = useCallback(async () => {
		resetFeatureRequestForm()

		if (!note?.id || !user?.id) {
			onDismiss()
			return
		}

		setDismissing(true)
		try {
			await markReleaseNoteSeen({ path: { note_id: note.id } })
			login({
				...user,
				last_seen_release_note_id: note.id,
			})
		} catch (error) {
			// Persisting "seen" is best-effort: always close the dialog so a
			// transient failure never traps the user behind a re-opening popup.
			captureUnexpectedError(error)
		} finally {
			setDismissing(false)
			onDismiss()
		}
	}, [login, note?.id, onDismiss, resetFeatureRequestForm, user])

	const handleFeatureRequestCancel = () => {
		resetFeatureRequestForm()
	}

	const handleFeatureRequestSubmit = () => {
		if (!note?.version) {
			return
		}

		const trimmed = description.trim()
		if (!trimmed) {
			setDescriptionError(true)
			return
		}

		setDescriptionError(false)
		setSubmitting(true)
		try {
			const eventId = submitWhatsNewFeatureRequestFeedback(note.version, trimmed, user)
			if (!eventId) {
				toast.error(t('whatsNew.featureRequestError'))
				return
			}

			toast.success(t('whatsNew.featureRequestSuccess'))
			resetFeatureRequestForm()
		} catch {
			toast.error(t('whatsNew.featureRequestError'))
		} finally {
			setSubmitting(false)
		}
	}

	if (!note) {
		return null
	}

	const featureRequestAvailable = isSentryUserFeedbackAvailable()

	return (
		<Dialog
			open={open}
			onOpenChange={(nextOpen) => {
				if (!nextOpen) {
					void handleDismiss()
				}
			}}
		>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{note.title}</DialogTitle>
				</DialogHeader>

				<div className="py-1">
					<WhatsNewMarkdown>{note.body}</WhatsNewMarkdown>
				</div>

				{showFeatureRequest ? (
					<div className="space-y-3 border-t pt-4">
						<div className="space-y-2">
							<Label htmlFor="whats-new-feature-request">{t('whatsNew.featureRequestLabel')}</Label>
							<Textarea
								id="whats-new-feature-request"
								value={description}
								onChange={(event) => {
									setDescription(event.target.value)
									if (descriptionError && event.target.value.trim()) {
										setDescriptionError(false)
									}
								}}
								placeholder={t('whatsNew.featureRequestPlaceholder')}
								rows={4}
								disabled={submitting || dismissing}
								aria-invalid={descriptionError}
							/>
							{descriptionError ? (
								<p className="text-sm text-destructive">{t('whatsNew.featureRequestRequired')}</p>
							) : null}
						</div>

						<div className="flex flex-wrap justify-end gap-2">
							<Button
								type="button"
								variant="outline"
								disabled={submitting || dismissing}
								onClick={handleFeatureRequestCancel}
							>
								{t('whatsNew.featureRequestCancel')}
							</Button>
							<Button
								type="button"
								disabled={submitting || dismissing}
								onClick={handleFeatureRequestSubmit}
							>
								{t('whatsNew.featureRequestSubmit')}
							</Button>
						</div>
					</div>
				) : null}

				<DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
					{featureRequestAvailable && !showFeatureRequest ? (
						<Button
							type="button"
							variant="link"
							className="h-auto px-0 text-left"
							disabled={dismissing || submitting}
							onClick={() => setShowFeatureRequest(true)}
						>
							{t('whatsNew.askFeature')}
						</Button>
					) : (
						<span />
					)}

					<Button disabled={dismissing || submitting} onClick={() => void handleDismiss()}>
						{t('whatsNew.dismiss')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export function WhatsNewHost() {
	const { note, shouldShow, dismiss } = useWhatsNew()

	return <WhatsNewDialog open={shouldShow} note={note} onDismiss={dismiss} />
}
